import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { ExtensionTypes } from 'generated/graphql-types';
import { Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { AppConfigService } from 'src/app/app-config/app-config-service';
import { userPreferencesRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { getShowTopBar } from 'src/app/app.selectors';
import { CurrentUser } from 'src/app/authentication/models/current-user.interface';
import { logout, updateIsSystemUser } from 'src/app/authentication/state/authentication.actions';
import {
   getCurrentUser,
   getCurrentUserIsSystemUser,
   selectCurrentUserIsLocal,
} from 'src/app/authentication/state/authentication.selector';
import { extensionLoadQueuesActions } from 'src/app/common-components/entity-select/extension/state/extension-option.actions';
import { createExtensionSuccess } from 'src/app/pages/add-extension/state/add-extension.actions';
import { deleteExtensionSuccess } from 'src/app/pages/extension-details/state/extension-detail.actions';
import {
   getIsOnSystemPage,
   getSelectedAccountId,
   getSelectedAccountName,
} from 'src/app/store/select-account/select-account.selector';
import {
   saveExpandMenu,
   saveShowExtensionRegisterPopup,
} from 'src/app/store/user-preferences/user-preferences.actions';
import {
   getExpandMenuUserPreference,
   getShowExtensionRegisterPopupUserPreference,
} from 'src/app/store/user-preferences/user-preferences.selectors';
import { MenuItem } from '../site-menu/menu-item';
import { SiteEndSidenavRef } from '../site-sidenav/site-end-sidenav-ref';
import { SiteStartSidenavRef } from '../site-sidenav/site-start-sidenav-ref';
import { customerViewClick } from '../state/site-layout.actions';
import { selectMenuItems } from '../state/site-layout.selectors';
import { SiteLayoutViewComponent } from '../views/site-layout-view.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-site-layout',
    templateUrl: './site-layout.component.html',
    standalone: false
})
export class SiteLayoutComponent implements OnInit, OnDestroy {
   @ViewChild(MatSidenavContainer, { static: true }) sidenavContainer: MatSidenavContainer;

   @ViewChild('startSidenav', { static: true }) startSidenav: MatSidenav;

   @ViewChild('endSidenav', { static: true }) endSidenav: MatSidenav;

   @ViewChild(SiteLayoutViewComponent, { static: true }) siteLayoutViewComponent: SiteLayoutViewComponent;

   currentUser$: Observable<CurrentUser | null>;

   currentUserHasAccessToAnyTicketTypes$: Observable<boolean>;

   menuItems$: Observable<MenuItem[]>;

   menuExpanded$: Observable<boolean>;

   selectedAccountName$: Observable<string | null>;

   externalAccountNumber$: Observable<number | undefined>;

   mobileView$: Observable<boolean>;

   tabletView$: Observable<boolean>;

   showCallQueues$: Observable<boolean>;

   showTopBar$: Observable<boolean>;

   isLocalUser$: Observable<boolean | null>;

   isSystemUser$: Observable<boolean>;

   isOnSystemPage$: Observable<boolean | undefined>;

   searchText$: Observable<string>;

   showExtensionRegisterPopup$: Observable<boolean>;

   private destroyed$ = new Subject<void>();

   adminToken$: Observable<boolean>;

   constructor(
      private actions$: Actions,
      private store: Store<AppState>,
      private siteStartSidenavRef: SiteStartSidenavRef,
      private siteEndSidenavRef: SiteEndSidenavRef,
      private router: Router,
      private appConfigService: AppConfigService
   ) {
      this.currentUser$ = this.store.pipe(select(getCurrentUser));
      this.menuItems$ = this.store.pipe(select(selectMenuItems));
      this.showTopBar$ = this.store.pipe(select(getShowTopBar));
      this.isLocalUser$ = this.store.pipe(select(selectCurrentUserIsLocal));
      this.selectedAccountName$ = this.store.pipe(select(getSelectedAccountName));
      this.isOnSystemPage$ = this.store.pipe(select(getIsOnSystemPage));
      this.adminToken$ = of(this.appConfigService.getConfig().adminToken ? true : false);
      this.menuExpanded$ = this.store.pipe(
         select(getExpandMenuUserPreference),
         map((isExpanded) => {
            return this.appConfigService.getConfig().adminToken ? true : isExpanded ?? false;
         })
      );
      this.showExtensionRegisterPopup$ = this.store.pipe(
         select(getShowExtensionRegisterPopupUserPreference),
         map((isTooltipsShown) => isTooltipsShown ?? true)
      );
      this.isSystemUser$ = this.store.pipe(select(getCurrentUserIsSystemUser));
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   ngOnInit(): void {
      this.siteStartSidenavRef.initialize(this.sidenavContainer, this.startSidenav);
      this.siteEndSidenavRef.initialize(this.sidenavContainer, this.endSidenav);
      this.store
         .pipe(select(getSelectedAccountId), distinctUntilChanged(), takeUntil(this.destroyed$))
         .subscribe((accountId) => {
            this.loadCallQueueExtensions(accountId);
         });
      this.actions$
         .pipe(
            ofType(createExtensionSuccess, deleteExtensionSuccess),
            withLatestFrom(this.store.pipe(select(getSelectedAccountId))),
            takeUntil(this.destroyed$)
         )
         .subscribe(([, accountId]) => {
            this.loadCallQueueExtensions(accountId);
         });
   }

   onSignOutClick(): void {
      this.store.dispatch(logout());
   }

   onCustomerViewClicked(isChecked: boolean): void {
      this.store.dispatch(customerViewClick({ checked: isChecked }));
      this.store.dispatch(updateIsSystemUser({ isSystemUser: !isChecked }));
   }

   onToggleMenuClicked(isExpanded: boolean): void {
      this.store.dispatch(saveExpandMenu({ expandMenu: isExpanded }));
   }

   onUserPreferencesClicked(): void {
      this.router.navigate([userPreferencesRoute]);
   }

   toggleShowExtensionRegisterPopup(isTooltipsShown: boolean): void {
      this.store.dispatch(saveShowExtensionRegisterPopup({ showExtensionRegisterPopup: isTooltipsShown }));
   }

   private loadCallQueueExtensions(accountId: number | null): void {
      if (accountId == null) {
         return;
      }

      this.store.dispatch(
         extensionLoadQueuesActions.load({
            filterOverrides: [
               `accountId = ${accountId}`,
               `type = ${ExtensionTypes.CallQueue}`,
               'isArchived = false',
            ],
            useIntermediateEmptyState: true,
         })
      );
   }
}
